import type { Product } from '@scayle/storefront-nuxt'
import { applyOdoscopeCookies } from '@goldner-group/storefront-odoscope'
import type { OdoscopeRecommendationRpcResult } from '@goldner-group/storefront-odoscope/types'
import { useState } from '#app/composables'
import { useNuxtApp } from '#app/nuxt'
import { useRpc } from '#storefront/composables'
import type { OdoscopeRecommendationsToken } from '#layers/base/constants/recommendations'
import { shapeProductCardPayloads } from '#layers/base/utils/productPayload'
import {
  DEFAULT_PRICE_PROMOTION_KEY,
  DEFAULT_PRODUCT_WITH,
} from '#layers/base/constants/withParameters'

export default (
  token: OdoscopeRecommendationsToken,
  categoryId?: number,
  productId?: number,
) => {
  const log = useLog('useOdoscopeRecommendations')
  const nuxtApp = useNuxtApp()

  // Create a unique key for each recommendation type + context
  const key = `useOdoscopeRecommendations-${token}-${categoryId ?? 'noCat'}-${productId ?? 'noProd'}`
  const recommendations = useState<Product[]>(key, () => [])
  const loading = useState(`${key}-loading`, () => false)
  const fetching = useState(`${key}-fetching`, () => false)
  const error = useState(`${key}-error`, () => false)
  // Odoscope control-group flag. The shared fetch still returns products for
  // control users, so we expose this so callers can suppress tracking/rendering
  // for the control group (parity with AGS's reco gate).
  const goControl = useState<boolean>(`${key}-goControl`, () => false)
  const hasFetched = useState<boolean>(`${key}-hasFetched`, () => false)

  const retry = async <T>(
    fn: () => Promise<T>,
    retries = 3,
    delay = 1000,
  ): Promise<T> => {
    let lastError: any
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn()
      } catch (err: any) {
        lastError = err
        if (
          attempt === retries ||
          !(err?.status === 408 || err?.name === 'FetchError')
        ) {
          break
        }
        // setTimeout is the canonical way to delay; promise wrapper required.
        // eslint-disable-next-line promise/avoid-new
        await new Promise<void>((resolve) => setTimeout(resolve, delay))
      }
    }
    throw lastError
  }

  const fetchRecommendations = async () => {
    if (nuxtApp.$device?.isCrawler) {
      hasFetched.value = true
      recommendations.value = []
      return recommendations.value
    }

    if (recommendations.value.length || fetching.value || hasFetched.value) {
      return recommendations.value
    }

    fetching.value = true
    loading.value = true
    error.value = false

    const fetchRecommendationResult = async (): Promise<
      OdoscopeRecommendationRpcResult | undefined
    > => {
      const {
        data,
        error: rpcError,
        refresh,
      } = useRpc(
        'getOdoscopeRecommendations',
        key,
        {
          tokenSlot: token,
          categoryId,
          productId,
          limit: 16,
          with: DEFAULT_PRODUCT_WITH,
          pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
          useGermanSiteId: true,
        },
        { immediate: false, lazy: false, server: false } as any,
      )

      await refresh()

      if (rpcError.value) {
        throw rpcError.value
      }

      applyOdoscopeCookies(data.value?.oscCookie)
      return data.value || undefined
    }

    try {
      const response = await retry(fetchRecommendationResult)
      recommendations.value = shapeProductCardPayloads(response?.products ?? [])
      goControl.value = Boolean(response?.originalResponse?.goControl)
      error.value =
        response?.odoscopeEvent?.action === 'error' ||
        response?.odoscopeEvent?.action === 'timeout'
      hasFetched.value = true
    } catch (err) {
      log.error('Error fetching recommendations', err)
      recommendations.value = []
      error.value = true
      goControl.value = false
      hasFetched.value = true
    } finally {
      fetching.value = false
      loading.value = false
    }

    return recommendations.value
  }

  return { recommendations, loading, error, fetchRecommendations, goControl }
}
